import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useAuth } from "../../context/AuthContext";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

const parseList = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const computeStatus = (h: Hackathon) => {
  const now = new Date();
  if (h.startDate && h.endDate) {
    const startDate = new Date(h.startDate);
    const endDate = new Date(h.endDate);
    if (now < startDate) return "Upcoming";
    if (now >= startDate && now <= endDate) return "Active";
    return "Completed";
  }
  return "Completed";
};

interface Hackathon { _id: string; title: string; startDate?: string; endDate?: string; }
interface Team { _id: string; teamName: string; mentorId?: { _id: string; name: string } | null; leader?: { name: string }; }
interface Mentor { _id: string; name: string; email: string; }

interface Assignment {
  teamId: string;
  teamName: string;
  hackathonId: string;
  hackathonTitle: string;
  mentor: { _id: string; name: string };
}

export function MentorAssignment() {
  const { token } = useAuth();

  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ hackathonId: "", teamId: "", mentorId: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch hackathons
  useEffect(() => {
    fetch(`${BASE}/hackathon-events?limit=100`)
      .then(r => r.json())
      .then(d => setHackathons(parseList<Hackathon>(d)))
      .catch(console.error);
  }, []);

  // Fetch mentors
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/admin/members?role=mentor`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setMentors(parseList<Mentor>(d)))
      .catch(console.error);
  }, [token]);

  // Fetch all existing assignments on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/admin/teams`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const allTeams: Team[] = parseList<Team>(d);
        const existing: Assignment[] = allTeams
          .filter(t => t.mentorId)
          .map(t => {
            const hackathonId = String((t as any).hackathonId?._id ?? (t as any).hackathonId ?? "");
            const hackathonTitle = (t as any).hackathonId?.title ?? `Hackathon ${hackathonId}`;

            return {
              teamId: t._id,
              teamName: t.teamName,
              hackathonId: hackathonId,
              hackathonTitle: hackathonTitle,
              mentor: { _id: (t.mentorId as any)._id, name: (t.mentorId as any).name },
            };
          });

        console.log("Existing mentor assignments:", existing);
        setAssignments(existing);
      })
      .catch(console.error);
  }, [token]);

  // Fetch teams when hackathon selected in form
  useEffect(() => {
    if (!token || !form.hackathonId) { setTeams([]); return; }
    fetch(`${BASE}/admin/teams?hackathonId=${form.hackathonId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const allTeamsInHackathon: Team[] = parseList<Team>(d);
        console.log(`Fetched ${allTeamsInHackathon.length} teams for hackathon ${form.hackathonId}:`, allTeamsInHackathon);
        setTeams(allTeamsInHackathon);
      })
      .catch(console.error);
  }, [form.hackathonId, token]);

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.hackathonId || !form.teamId || !form.mentorId) {
      setError("All fields are required."); return;
    }
    setSubmitting(true); setError(""); setSuccess("");
    try {
      const res = await fetch(`${BASE}/admin/teams/assign-mentor`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ teamId: form.teamId, mentorId: form.mentorId, hackathonId: form.hackathonId, forceReplace: true }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message ?? "Failed to assign mentor"); return; }

      const mentor = mentors.find(m => m._id === form.mentorId)!;
      const team = teams.find(t => t._id === form.teamId)!;
      const hackathon = hackathons.find(h => h._id === form.hackathonId)!;

      setAssignments(prev => {
        const filtered = prev.filter(a => a.teamId !== form.teamId);
        return [...filtered, {
          teamId: team._id,
          teamName: team.teamName,
          hackathonId: hackathon._id,
          hackathonTitle: hackathon.title,
          mentor: { _id: mentor._id, name: mentor.name }
        }];
      });
      setTeams(prev => prev.map(t => t._id === form.teamId ? { ...t, mentorId: { _id: mentor._id, name: mentor.name } } : t));
      setSuccess("Mentor assigned successfully.");
      setTimeout(() => setSuccess(""), 3000);
      setShowForm(false);
      setForm(p => ({ ...p, teamId: "", mentorId: "" }));
    } catch (err: any) {
      setError(err.message ?? "Failed to assign mentor");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async (teamId: string) => {
    setError("");
    try {
      const res = await fetch(`${BASE}/admin/teams/unassign-mentor`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ teamId }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message ?? "Failed to unassign mentor"); return; }
      setAssignments(prev => prev.filter(a => a.teamId !== teamId));
      setTeams(prev => prev.map(t => t._id === teamId ? { ...t, mentorId: null } : t));
    } catch (err: any) {
      setError(err.message ?? "Failed to unassign mentor");
    }
  };

  return (
    <div className="px-[12px]">
      <div className="mb-[32px] flex items-center justify-between py-[32px]">
        <div>
          <h1 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[42px] text-[#1a2332] text-[28px]">
            Mentor Assignment
          </h1>
          <p className="font-['Inter:Regular',sans-serif] font-normal leading-[21px] text-[#6e7891] text-[14px]">
            Assign mentors to teams for guidance and feedback.
          </p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setError(""); }}
          className="px-[24px] py-[12px] bg-[#0058ab] text-white rounded-[14px] font-['Inter:Medium',sans-serif] font-medium text-[14px] shadow-[0px_4px_12px_0px_rgba(0,88,171,0.25)] hover:bg-[#004a94] transition-colors"
        >
          {showForm ? "Cancel" : "Assign Mentor"}
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded-[8px] text-[14px]">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-50 border border-green-300 text-green-700 rounded-[8px] text-[14px]">{success}</div>}

      {showForm && (
        <div className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[32px] mb-[32px]"
          style={{ backgroundImage: "linear-gradient(157.593deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}>
          <h3 className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[18px] mb-[24px]">
            Assign Mentor to Team
          </h3>
          <form onSubmit={handleAssign} className="flex flex-col gap-[20px]">
            <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-3">

              {/* Hackathon */}
              <div className="flex flex-col gap-[8px]">
                <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">
                  Hackathon<span className="text-red-500 ml-1">*</span>
                </label>
                <Select value={form.hackathonId} onValueChange={v => setForm(p => ({ ...p, hackathonId: v, teamId: "" }))}>
                  <SelectTrigger className="bg-white border border-[#e0e4f0] rounded-[8px]">
                    <SelectValue placeholder="Select hackathon" />
                  </SelectTrigger>
                  <SelectContent>
                    {hackathons.length === 0
                      ? <SelectItem value="none" disabled>No hackathons found</SelectItem>
                      : (() => {
                          const statusOrder = { Active: 0, Upcoming: 1, Completed: 2 };
                          const sorted = [...hackathons].sort((a, b) => {
                            const statusA = computeStatus(a);
                            const statusB = computeStatus(b);
                            return (statusOrder[statusA as keyof typeof statusOrder] ?? 99) - (statusOrder[statusB as keyof typeof statusOrder] ?? 99);
                          });
                          return sorted.map(h => <SelectItem key={h._id} value={h._id}>{h.title}</SelectItem>);
                        })()
                    }
                  </SelectContent>
                </Select>
              </div>

              {/* Team */}
              <div className="flex flex-col gap-[8px]">
                <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">
                  Team<span className="text-red-500 ml-1">*</span>
                </label>
                <Select value={form.teamId} onValueChange={v => setForm(p => ({ ...p, teamId: v }))} disabled={!form.hackathonId}>
                  <SelectTrigger className="bg-white border border-[#e0e4f0] rounded-[8px]">
                    <SelectValue placeholder={form.hackathonId ? "Select team" : "Select hackathon first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.length === 0
                      ? <SelectItem value="none" disabled>No teams found</SelectItem>
                      : teams.map(t => <SelectItem key={t._id} value={t._id}>{t.teamName}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>

              {/* Mentor */}
              <div className="flex flex-col gap-[8px]">
                <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">
                  Mentor<span className="text-red-500 ml-1">*</span>
                </label>
                <Select value={form.mentorId} onValueChange={v => setForm(p => ({ ...p, mentorId: v }))}>
                  <SelectTrigger className="bg-white border border-[#e0e4f0] rounded-[8px]">
                    <SelectValue placeholder="Select mentor" />
                  </SelectTrigger>
                  <SelectContent>
                    {mentors.length === 0
                      ? <SelectItem value="none" disabled>No mentors found</SelectItem>
                      : mentors.map(m => <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-[12px] justify-end pt-[12px]">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-[20px] py-[10px] bg-white border border-[#6e7891] text-[#6e7891] rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#f5f7fb] transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="px-[20px] py-[10px] bg-[#0058ab] text-white rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#004a94] transition-colors disabled:opacity-60">
                {submitting ? "Assigning..." : "Assign Mentor"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Assignments list */}
      <div className="flex flex-col gap-[16px]">
        {assignments.length === 0 && (
          <p className="text-[#6e7891] text-[14px]">No mentor assignments found.</p>
        )}
        {assignments.map(a => (
          <div key={a.teamId}
            className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[24px]"
            style={{ backgroundImage: "linear-gradient(157.593deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}>
            <div className="flex items-start justify-between">
              <div className="flex-1 grid grid-cols-3 gap-[24px]">
                <div>
                  <p className="font-['Inter:Medium',sans-serif] font-medium text-[#8b93a8] text-[11px] tracking-[0.275px] uppercase mb-[8px]">Hackathon</p>
                  <p className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[14px]">{a.hackathonTitle}</p>
                </div>
                <div>
                  <p className="font-['Inter:Medium',sans-serif] font-medium text-[#8b93a8] text-[11px] tracking-[0.275px] uppercase mb-[8px]">Team</p>
                  <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#0058ab] text-[14px]">{a.teamName}</p>
                </div>
                <div>
                  <p className="font-['Inter:Medium',sans-serif] font-medium text-[#8b93a8] text-[11px] tracking-[0.275px] uppercase mb-[8px]">Mentor</p>
                  <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#00d68f] text-[14px]">{a.mentor.name}</p>
                </div>
              </div>
              <button onClick={() => handleUnassign(a.teamId)}
                className="px-[16px] py-[8px] bg-[#ff816e] text-white rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#ff6b56] transition-colors">
                Unassign
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
